import type { ProBuilderConnection } from '~/hooks/queries/use-pro-builder';

/**
 * The Pro Builder terminal's lifetime (FRONT-3003, FRONT-3040), as a pure
 * reducer. Saving a new connection never touches a running Claude Code: the
 * terminal is remounted (a new `terminalKey`, which ends the old shell and
 * opens a fresh one with the new environment) ONLY when the person clicks
 * Restart terminal in Settings. Hiding the window, unmounting it and reloading
 * the page only DETACH; the next open attaches to the same shell.
 *
 * `startedWith` is the connection the RUNNING shell was started with, and is
 * null whenever no terminal is open. It is set only by `terminal-opened`, which
 * the terminal dispatches after EVERY successful open from the OPEN RESPONSE:
 * the server reports what that shell was SPAWNED with, so an attach (after a
 * reload, or in a second tab) records the original credential, never the
 * person's current one. It is cleared by `terminal-stopped` (the terminal
 * unmounted: the organization switch went off, Pro Builder's flag went off, or
 * a restart is replacing it) and by `restart` itself, so Settings never offers
 * to restart a terminal that is not open.
 *
 * Whether a restart is owed has two sources, either of which shows the offer:
 *  - `restartPending`: a save in Settings changed the credential the running
 *    shell uses, in this tab, a moment ago. It is cleared only by a spawn
 *    (`attached: false`) or `restart`, never by an attach: an attach re-uses
 *    the old shell, which still carries the old credential;
 *  - `startedWith` against the current status, compared on what a terminal's
 *    environment is actually built from (`terminalCredential`, plus the
 *    credential tag for a replaced key) — this also catches a save made from
 *    another tab or before a reload.
 *
 * The state lives in `pro-builder-session-store.ts`, so the window (which owns
 * the terminal) and Settings (which offers the restart) read ONE source.
 */
export interface ConnectionSnapshot {
  mode: ProBuilderConnection['mode'];
  hasApiKey: boolean;
  hasBedrock: boolean;
  bedrockRegion: string | null;
  /**
   * The server's non-reversible tag of the credential the environment carries
   * (null for none). Two equal tags mean the same credential, so a REPLACED key
   * — which leaves every flag above unchanged — still differs.
   */
  credentialTag: string | null;
  /**
   * FRONT-3228: the auto-compact threshold the shell was started with (null = Claude Code's
   * default). Carried for the header's own restart row only — `restartOwed`, `connectionDiffers`
   * and `sameSnapshot` deliberately never read it, so Settings' credential offer is unchanged.
   * Absent on a hand-built snapshot of the CURRENT connection; a missing value reads as null.
   */
  autoCompactPct?: number | null;
}

export interface TerminalSessionState {
  /** The terminal component's React key: a new value remounts it. */
  terminalKey: number;
  /** A save in Settings changed the credential the running shell uses, since it started. */
  restartPending: boolean;
  /** What the running shell was started with; null while no shell is running. */
  startedWith: ConnectionSnapshot | null;
}

/** A credential a save can set or remove. */
export type ProBuilderCredential = 'api_key' | 'bedrock';

export type TerminalSessionAction =
  | { type: 'connection-saved'; touched: readonly ProBuilderCredential[] }
  | { type: 'terminal-opened'; connection: ConnectionSnapshot; attached: boolean }
  | { type: 'terminal-stopped' }
  | { type: 'restart' };

export const INITIAL_TERMINAL_SESSION: TerminalSessionState = {
  terminalKey: 0,
  restartPending: false,
  startedWith: null,
};

/** The person's CURRENT saved connection (a status read) as a snapshot. */
export function snapshotConnection(c: ProBuilderConnection, credentialTag: string | null | undefined): ConnectionSnapshot {
  return {
    mode: c.mode,
    hasApiKey: c.hasApiKey,
    hasBedrock: c.hasBedrock,
    bedrockRegion: c.bedrockRegion,
    credentialTag: credentialTag ?? null,
  };
}

const MODES: readonly ProBuilderConnection['mode'][] = ['subscription', 'api_key', 'bedrock'];

/**
 * `startedWith` from an open response, or null when it is absent or malformed
 * (then nothing is recorded: no claim beats a wrong one).
 */
export function parseStartedWith(raw: unknown): ConnectionSnapshot | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (!MODES.includes(r.mode as ProBuilderConnection['mode'])) return null;
  if (typeof r.hasApiKey !== 'boolean' || typeof r.hasBedrock !== 'boolean') return null;
  if (r.bedrockRegion !== null && typeof r.bedrockRegion !== 'string') return null;
  if (r.credentialTag !== null && typeof r.credentialTag !== 'string') return null;
  return {
    mode: r.mode as ProBuilderConnection['mode'],
    hasApiKey: r.hasApiKey,
    hasBedrock: r.hasBedrock,
    bedrockRegion: r.bedrockRegion,
    credentialTag: r.credentialTag,
    // An older server (or a row written before the field) sends none: that is the default, null.
    autoCompactPct: typeof r.autoCompactPct === 'number' ? r.autoCompactPct : null,
  };
}

/**
 * The credential a terminal started with this connection runs on, or null for
 * none (Claude Code signs in by itself). Mirrors the server's
 * `buildProBuilderEnv` (`src/pro-builder/env.ts`): only the current mode's own
 * credential reaches the shell, and a mode whose credential is missing runs
 * as the subscription. `pro-builder-terminal-session.test.ts` executes both
 * (and the server's credential tag) over every combination.
 */
export function terminalCredential(c: ConnectionSnapshot): ProBuilderCredential | null {
  if (c.mode === 'api_key' && c.hasApiKey) return 'api_key';
  if (c.mode === 'bedrock' && c.hasBedrock && !!c.bedrockRegion) return 'bedrock';
  return null;
}

/** Whether a terminal started with `b` would get a different environment from one started with `a`. */
export function connectionDiffers(a: ConnectionSnapshot, b: ConnectionSnapshot): boolean {
  const kind = terminalCredential(a);
  if (kind !== terminalCredential(b)) return true;
  if (kind === 'bedrock' && a.bedrockRegion !== b.bedrockRegion) return true;
  // Same kind of credential on both sides: a replaced key or secret is the same
  // flags with a different tag. No credential (subscription) carries no tag.
  return kind !== null && a.credentialTag !== b.credentialTag;
}

/** Which credentials a `PUT /api/pro-builder/connection` body sets or removes (absent leaves one unchanged). */
export function touchedCredentials(body: { apiKey?: unknown; bedrock?: unknown }): ProBuilderCredential[] {
  const touched: ProBuilderCredential[] = [];
  if (body.apiKey !== undefined) touched.push('api_key');
  if (body.bedrock !== undefined) touched.push('bedrock');
  return touched;
}

/** Whether two snapshots describe the same spawn (every field equal, the credential tag included). */
function sameSnapshot(a: ConnectionSnapshot, b: ConnectionSnapshot): boolean {
  return (
    a.mode === b.mode &&
    a.hasApiKey === b.hasApiKey &&
    a.hasBedrock === b.hasBedrock &&
    a.bedrockRegion === b.bedrockRegion &&
    a.credentialTag === b.credentialTag
  );
}

/** True when the offer to restart the terminal must show. */
export function restartOwed(state: TerminalSessionState, current: ConnectionSnapshot): boolean {
  // No shell running means nothing to restart.
  if (state.startedWith === null) return false;
  return state.restartPending || connectionDiffers(state.startedWith, current);
}

export function terminalSessionReducer(state: TerminalSessionState, action: TerminalSessionAction): TerminalSessionState {
  switch (action.type) {
    case 'connection-saved': {
      if (state.restartPending || state.startedWith === null) return state;
      // Only the credential the running shell uses can make it stale; removing
      // or setting the other one changes nothing a restart would pick up.
      const running = terminalCredential(state.startedWith);
      return running && action.touched.includes(running) ? { ...state, restartPending: true } : state;
    }
    case 'terminal-opened': {
      // An attach to the SAME shell this state was tracking (same spawn snapshot) still owes the
      // restart a save made since it spawned. An attach to a DIFFERENT shell (another tab restarted
      // onto a new one) owes nothing: the flag described the old shell, so the credentialTag
      // comparison against the new shell's own snapshot decides.
      const sameShell = state.startedWith !== null && sameSnapshot(state.startedWith, action.connection);
      return { ...state, startedWith: action.connection, restartPending: action.attached && sameShell ? state.restartPending : false };
    }
    case 'terminal-stopped':
      return state.startedWith === null && !state.restartPending ? state : { ...state, startedWith: null, restartPending: false };
    case 'restart':
      return { terminalKey: state.terminalKey + 1, restartPending: false, startedWith: null };
  }
}

/**
 * FRONT-3040: whether a Pro Builder terminal being torn down must END its
 * shell. The session outlives the tab, so the default is no (detach only);
 * the one exception is a restart — Settings' Restart terminal bumps the
 * store's `terminalKey` past the key this terminal mounted with, and the old
 * shell must be ended explicitly before the new terminal opens. Reload, tab
 * close, the window unmounting and Reconnect (whose shell is already gone)
 * leave the key alone and so detach.
 */
export function endsSessionOnTeardown(mountedKey: number, currentKey: number): boolean {
  return currentKey !== mountedKey;
}

/**
 * After a successful open: record what that shell was spawned with, as the
 * server reported it in the open response (an attach reports the ORIGINAL
 * spawn, not the current connection). `isCurrent` is false once a later open
 * or the terminal's unmount has superseded this one. A response without a
 * well-formed `startedWith` records nothing.
 */
export function recordOpenedSession(deps: {
  opened: { attached?: boolean; startedWith?: unknown };
  isCurrent: () => boolean;
  dispatch: (action: TerminalSessionAction) => void;
}): void {
  if (!deps.isCurrent()) return;
  const connection = parseStartedWith(deps.opened.startedWith);
  if (!connection) return;
  deps.dispatch({ type: 'terminal-opened', connection, attached: deps.opened.attached === true });
}
